import { useQuery as useApolloQuery } from '@apollo/client'
import { LastGamesList } from '@/components/organisms/LastGamesList/LastGamesList'
import { LAST_MINE_GAMES_QUERY, LAST_MINE_GAMES_BY_PLAYER_QUERY } from '@/api/query'
import { useAppModal, AppModals } from '@/context/ModalProvider'
import { Flex, SimpleGrid, Text } from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { useMinesStore } from './MinesStore'
import { GetMineGamesQuery, GetMineGamesByPlayerQuery } from '@/__generated__/graphql'
import { INJ_DENOM } from '@/constants/constants'
import { useTranslation } from 'next-i18next/pages'
import { useSession } from '@/context/SessionProvider'

export const MinesSidebar = () => {
  const modal = useAppModal()
  const { t } = useTranslation()
  const { account } = useSession()
  const address = account?.injectiveAddress
  const [onlyMine, setOnlyMine] = useState(false)

  const allQuery = useApolloQuery(LAST_MINE_GAMES_QUERY, {
    pollInterval: 10000,
    returnPartialData: true,
    skip: onlyMine,
  })

  const myQuery = useApolloQuery(LAST_MINE_GAMES_BY_PLAYER_QUERY, {
    pollInterval: 10000,
    returnPartialData: true,
    variables: { player: address || '' },
    skip: !onlyMine || !address,
  })

  const gameId = useMinesStore((x) => x.game?.id)

  useEffect(() => {
    if (onlyMine) myQuery.refetch()
    else allQuery.refetch()
  }, [gameId])

  const activeGames = onlyMine ? transformMinesGamesByPlayer(myQuery.data) : transformMinesGames(allQuery.data)
  const activeLoading = onlyMine ? myQuery.loading : allQuery.loading
  const activeError = onlyMine ? !!myQuery.error : !!allQuery.error

  return (
    <Flex h={'full'} p={{ base: 4, sm: 5 }} bg={'ninja.600'} rounded={'2xl'} flexDir={'column'}>
      <SimpleGrid
        flexShrink={0}
        gap={1}
        p={1}
        h={10}
        fontSize={'xs'}
        fontWeight={700}
        bg={'ninja.500'}
        rounded={'xl'}
        columns={address ? 2 : 1}
        mb={3}
      >
        <Flex
          onClick={() => setOnlyMine(false)}
          cursor={'pointer'}
          rounded={'lg'}
          bg={!onlyMine ? '#3D3C70' : 'transparent'}
          justifyContent={'center'}
          alignItems={'center'}
        >
          <Text color={!onlyMine ? 'white' : 'ninja.300'}>{t('common.last-games')}</Text>
        </Flex>
        {address && (
          <Flex
            onClick={() => setOnlyMine(true)}
            bg={onlyMine ? '#3D3C70' : 'transparent'}
            cursor={'pointer'}
            rounded={'lg'}
            justifyContent={'center'}
            alignItems={'center'}
          >
            <Text color={onlyMine ? 'white' : 'ninja.300'}>{t('common.my-games')}</Text>
          </Flex>
        )}
      </SimpleGrid>
      <LastGamesList
        gameDetailsCallback={(id) => modal.openModalNew(AppModals.MinesGameDetails, { gameId: id })}
        error={activeError}
        loading={activeLoading}
        games={activeGames}
        viewAllHref={onlyMine && address ? `/activity/${address}?game=mines` : undefined}
      />
    </Flex>
  )
}

const mapMinesGame = (game: any) => ({
  id: (game?.id || '') as string,
  player: game?.player || '',
  status: game?.status || '',
  payout: { amount: (game?.payable || '') as string, denom: game?.denom || INJ_DENOM },
  bet: { amount: (game?.bet || '') as string, denom: game?.denom || INJ_DENOM },
  betUsd: game?.betUsd != null ? String(game.betUsd) : undefined,
  payoutUsd: game?.payoutUsd != null ? String(game.payoutUsd) : undefined,
})

const transformMinesGames = (data?: GetMineGamesQuery) => {
  if (!data?.minesGames) return []
  return data.minesGames.nodes.map(mapMinesGame).reverse()
}

const transformMinesGamesByPlayer = (data?: GetMineGamesByPlayerQuery) => {
  if (!data?.minesGames) return []
  return data.minesGames.nodes.map(mapMinesGame).reverse()
}
